import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { getRequestSubjectDisplayZeroFee, getRequestSubjectFee, useHasFF } from 'venn-utils';
import { displayZeroFeeState, isReportState, type StudioRequestSubject, useCachedLoadableValue } from 'venn-state';

export type GetRequestSubjectDisplayZeroFee = (params: {
  subject: StudioRequestSubject;
  isBenchmark?: boolean;
}) => boolean;

export const useGetRequestSubjectDisplayZeroFee = () => {
  const hasDefaultRLAdvisoryFeesFF = useHasFF('default_rl_advisory_fees_ff');
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const configValue = useCachedLoadableValue(displayZeroFeeState, false);
  const isReport = useRecoilValue(isReportState);

  return useCallback<GetRequestSubjectDisplayZeroFee>(
    ({ subject, isBenchmark }: { subject: StudioRequestSubject; isBenchmark?: boolean }) => {
      return getRequestSubjectDisplayZeroFee({
        displayZeroFeeConfigValue: configValue,
        subject,
        displayDefaultFee: hasDefaultRLAdvisoryFeesFF,
        displayInvestmentFee: hasAdvisoryFeesForRlInvestmentsFF,
        isReport,
        isBenchmark,
      });
    },
    [configValue, hasAdvisoryFeesForRlInvestmentsFF, hasDefaultRLAdvisoryFeesFF, isReport],
  );
};

export type GetRequestSubjectFee = (params: {
  subject: StudioRequestSubject;
  isBenchmark?: boolean;
}) => number | undefined;

export const useGetRequestSubjectFee = () => {
  const hasAdvisoryFeesForRlInvestmentsFF = useHasFF('advisory_fees_for_rl_investments_ff');
  const getRequestSubjectDisplayZeroFee = useGetRequestSubjectDisplayZeroFee();
  const isReport = useRecoilValue(isReportState);

  return useCallback<GetRequestSubjectFee>(
    ({ subject, isBenchmark }: { subject: StudioRequestSubject; isBenchmark?: boolean }) => {
      return getRequestSubjectFee({
        subject,
        displayZeroFee: getRequestSubjectDisplayZeroFee({ subject, isBenchmark }),
        displayInvestmentFee: hasAdvisoryFeesForRlInvestmentsFF,
        isBenchmark,
        isReport,
      });
    },
    [getRequestSubjectDisplayZeroFee, hasAdvisoryFeesForRlInvestmentsFF, isReport],
  );
};
